import React, { useState, useRef, useEffect, useCallback } from 'react';
import '../styles/nassar-theme.css';
import './SearchableDropdown.css';

const SearchableDropdown = ({ 
    label, 
    options, 
    value, 
    onChange, 
    onAddNew,
    onEdit,
    onDelete,
    placeholder = 'اختر من القائمة...',
    addNewLabel = 'إضافة جديد',
    displayKey = 'name',
    valueKey = 'id',
    required = false,
    hasError = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, openAbove: false });
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const menuRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Calculate dropdown position
    const updatePosition = useCallback(() => {
        if (!inputRef.current) return;
        
        const rect = inputRef.current.getBoundingClientRect();
        const menuHeight = menuRef.current ? menuRef.current.offsetHeight : 300;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const gap = 4;

        let top;
        let openAbove = false;

        if (spaceBelow >= menuHeight + gap) {
            // Enough space below - show below
            top = rect.bottom + gap;
        } else if (spaceAbove >= menuHeight + gap) {
            // Enough space above - show above
            openAbove = true;
            top = rect.top - menuHeight - gap;
        } else if (spaceBelow >= spaceAbove) {
            // Not enough space either way - prefer below
            top = rect.bottom + gap;
        } else {
            // Not enough space either way - prefer above
            openAbove = true;
            top = rect.top - menuHeight - gap;
        }

        // Clamp to viewport bounds
        top = Math.max(8, Math.min(top, window.innerHeight - menuHeight - 8));

        setDropdownPosition({
            top,
            left: rect.left,
            width: rect.width,
            openAbove
        });
    }, []);

    // Calculate dropdown position when opened + listen for scroll/resize
    useEffect(() => {
        if (!isOpen || !inputRef.current) return;

        // Initial position calculation
        updatePosition();

        // Recalculate after render when we have actual menu height
        requestAnimationFrame(updatePosition);

        // Listen for scroll on any ancestor (captures modal scroll)
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);

        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen, updatePosition]);

    // Filter options based on search term
    const filteredOptions = options.filter(option =>
        option[displayKey]?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Get selected option display text
    const selectedOption = options.find(opt => opt[valueKey] === value);
    const displayText = selectedOption ? selectedOption[displayKey] : '';

    const handleSelect = (option) => {
        onChange(option[valueKey]);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleAddNew = () => {
        setIsOpen(false);
        if (onAddNew) {
            onAddNew();
        }
    };

    const handleEdit = (e, option) => {
        e.stopPropagation();
        if (onEdit) {
            onEdit(option);
        }
    };

    const handleDelete = (e, option) => {
        e.stopPropagation();
        if (onDelete) {
            onDelete(option);
        }
    };

    return (
        <div className="form-group">
            {label && (
                <label className="form-label">
                    {label}
                    {required && <span style={{ color: '#c33', marginRight: '4px' }}>*</span>}
                </label>
            )}
            
            <div className="searchable-dropdown" ref={dropdownRef}>
                <div 
                    ref={inputRef}
                    className={`dropdown-input ${isOpen ? 'open' : ''}`}
                    onClick={() => setIsOpen(!isOpen)}
                    style={{
                        borderColor: hasError ? '#dc3545' : undefined,
                        borderWidth: hasError ? '2px' : undefined
                    }}
                >
                    <span className={displayText ? '' : 'placeholder'}>
                        {displayText || placeholder}
                    </span>
                    <svg 
                        className={`dropdown-arrow ${isOpen ? 'rotate' : ''}`}
                        fill="currentColor" 
                        viewBox="0 0 20 20"
                    >
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                </div>

                {isOpen && (
                    <div 
                        ref={menuRef}
                        className={`dropdown-menu ${dropdownPosition.openAbove ? 'open-above' : ''}`}
                        style={{
                            top: `${dropdownPosition.top}px`,
                            left: `${dropdownPosition.left}px`,
                            width: `${dropdownPosition.width}px`
                        }}
                    >
                        <div className="dropdown-search">
                            <svg className="search-icon" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                            </svg>
                            <input
                                type="text"
                                className="search-input"
                                placeholder="بحث..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                            />
                        </div>

                        <div className="dropdown-options">
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map(option => (
                                    <div
                                        key={option[valueKey]}
                                        className={`dropdown-option ${option[valueKey] === value ? 'selected' : ''}`}
                                        onClick={() => handleSelect(option)}
                                    >
                                        <span className="option-text">{option[displayKey]}</span>
                                        <div className="option-actions">
                                            {option[valueKey] === value && (
                                                <svg className="check-icon" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                            {onEdit && (
                                                <button
                                                    type="button"
                                                    className="option-action-btn edit-btn"
                                                    onClick={(e) => handleEdit(e, option)}
                                                    title="تعديل"
                                                >
                                                    <svg fill="currentColor" viewBox="0 0 20 20">
                                                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                                    </svg>
                                                </button>
                                            )}
                                            {onDelete && (
                                                <button
                                                    type="button"
                                                    className="option-action-btn delete-btn"
                                                    onClick={(e) => handleDelete(e, option)}
                                                    title="حذف"
                                                >
                                                    <svg fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="no-options">
                                    لا توجد نتائج
                                </div>
                            )}
                        </div>

                        {onAddNew && (
                            <div className="dropdown-footer">
                                <button 
                                    type="button"
                                    className="add-new-btn"
                                    onClick={handleAddNew}
                                >
                                    <svg fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                    </svg>
                                    {addNewLabel}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SearchableDropdown;

